import React, { useEffect, useState } from "react";

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Button,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";

import {
  FiUser,
  FiMail,
  FiPhone,
  FiShield,
  FiSave,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

import api from "../../utills/api";


const Profile = () => {
  const [user, setUser] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  /* =========================================================
     LOAD PROFILE
  ========================================================= */

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      /*
       * There is currently no dedicated /profile backend
       * endpoint in the education backend.
       *
       * The authenticated user is already stored locally
       * after login, so use that as the profile source.
       */

      const storedUser =
        localStorage.getItem("user");

      if (!storedUser) {
        setError(
          "User profile information was not found."
        );

        return;
      }

      const parsedUser =
        JSON.parse(storedUser);

      setUser(parsedUser);

      setForm({
        name: parsedUser?.name || "",
        email: parsedUser?.email || "",
        phone: parsedUser?.phone || "",
      });

    } catch (err) {
      console.error(
        "Profile load error:",
        err
      );

      setError(
        "Unable to load your profile."
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadProfile();
  }, []);


  /* =========================================================
     INPUT CHANGE
  ========================================================= */

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setSuccess("");
    setError("");
  };


  /* =========================================================
     SAVE PROFILE
  ========================================================= */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      /*
       * The current backend does not expose a dedicated
       * authenticated self-profile update endpoint.
       *
       * Therefore we do not invent a PUT /profile API.
       *
       * Update the locally stored authenticated user so
       * the profile infrastructure is ready.
       */

      const updatedUser = {
        ...(user || {}),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      };

      localStorage.setItem(
        "user",
        JSON.stringify(updatedUser)
      );

      setUser(updatedUser);

      setSuccess(
        "Profile information updated successfully."
      );

    } catch (err) {
      console.error(
        "Profile save error:",
        err
      );

      setError(
        "Unable to save profile information."
      );
    } finally {
      setSaving(false);
    }
  };


  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("authUser");

    delete api.defaults.headers.common[
      "Authorization"
    ];

    window.location.href = "/login";
  };


  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "400px",
            }}
          >
            <Spinner size="sm" />
          </div>
        </Container>
      </div>
    );
  }


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-content">
      <Container fluid>

        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="d-flex justify-content-between align-items-center mb-4">

          <div>
            <h4 className="mb-1">
              My Profile
            </h4>

            <p className="text-muted mb-0">
              Manage your account information.
            </p>
          </div>

          <Button
            color="light"
            onClick={loadProfile}
            disabled={loading}
          >
            <FiRefreshCw
              size={15}
              className="me-2"
            />

            Refresh
          </Button>

        </div>


        {/* ===================================================
            ALERTS
        =================================================== */}

        {error && (
          <Alert color="danger">
            <FiAlertCircle
              className="me-2"
            />

            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success">
            <FiCheckCircle
              className="me-2"
            />

            {success}
          </Alert>
        )}


        <Row>

          {/* =================================================
              PROFILE CARD
          ================================================= */}

          <Col xl={4} lg={5}>

            <Card>

              <CardBody className="text-center">

                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                  style={{
                    width: "90px",
                    height: "90px",
                    fontSize: "32px",
                    fontWeight: 600,
                  }}
                >
                  {form.name
                    ? form.name
                        .split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map(
                          (item) =>
                            item
                              .charAt(0)
                              .toUpperCase()
                        )
                        .join("")
                    : "U"}
                </div>


                <h5 className="mb-1">
                  {form.name ||
                    "User"}
                </h5>


                <p className="text-muted mb-3">
                  {form.email ||
                    "No email"}
                </p>


                <Badge
                  color="primary"
                  pill
                  className="px-3 py-2"
                >
                  <FiShield
                    size={13}
                    className="me-1"
                  />

                  {user?.role ||
                    "User"}
                </Badge>


                <hr />


                <div className="text-start">

                  <div className="d-flex align-items-center mb-3">

                    <FiMail
                      size={16}
                      className="text-muted me-2"
                    />

                    <div>
                      <div className="text-muted small">
                        Email
                      </div>

                      <div>
                        {form.email ||
                          "-"}
                      </div>
                    </div>

                  </div>


                  <div className="d-flex align-items-center mb-3">

                    <FiPhone
                      size={16}
                      className="text-muted me-2"
                    />

                    <div>
                      <div className="text-muted small">
                        Phone
                      </div>

                      <div>
                        {form.phone ||
                          "Not provided"}
                      </div>
                    </div>

                  </div>


                  <div className="d-flex align-items-center">

                    <FiShield
                      size={16}
                      className="text-muted me-2"
                    />

                    <div>
                      <div className="text-muted small">
                        Role
                      </div>

                      <div>
                        {user?.role ||
                          "-"}
                      </div>
                    </div>

                  </div>

                </div>

              </CardBody>

            </Card>


            {/* ACCOUNT ACTIONS */}

            <Card>

              <CardBody>

                <h5 className="mb-3">
                  Account
                </h5>

                <p className="text-muted small">
                  You are currently signed in
                  to your education lead
                  management account.
                </p>

                <Button
                  color="danger"
                  outline
                  className="w-100"
                  onClick={
                    handleLogout
                  }
                >
                  Sign Out
                </Button>

              </CardBody>

            </Card>

          </Col>


          {/* =================================================
              PROFILE INFORMATION
          ================================================= */}

          <Col xl={8} lg={7}>

            <Card>

              <CardBody>

                <div className="d-flex align-items-center mb-4">

                  <div
                    className="avatar-sm me-3"
                  >
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                      <FiUser />
                    </span>
                  </div>

                  <div>

                    <h5 className="mb-1">
                      Personal Information
                    </h5>

                    <p className="text-muted mb-0">
                      Update the information
                      associated with your
                      account.
                    </p>

                  </div>

                </div>


                <Row>

                  {/* NAME */}

                  <Col md={6}>

                    <div className="mb-3">

                      <label className="form-label">
                        Full Name
                      </label>

                      <Input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={
                          handleChange
                        }
                        placeholder="Enter your name"
                      />

                    </div>

                  </Col>


                  {/* EMAIL */}

                  <Col md={6}>

                    <div className="mb-3">

                      <label className="form-label">
                        Email Address
                      </label>

                      <Input
                        type="email"
                        name="email"
                        value={
                          form.email
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="Enter your email"
                      />

                    </div>

                  </Col>


                  {/* PHONE */}

                  <Col md={6}>

                    <div className="mb-3">

                      <label className="form-label">
                        Phone Number
                      </label>

                      <Input
                        type="text"
                        name="phone"
                        value={
                          form.phone
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="Enter phone number"
                      />

                    </div>

                  </Col>


                  {/* ROLE */}

                  <Col md={6}>

                    <div className="mb-3">

                      <label className="form-label">
                        Role
                      </label>

                      <Input
                        type="text"
                        value={
                          user?.role ||
                          ""
                        }
                        disabled
                      />

                    </div>

                  </Col>

                </Row>


                <hr />


                <div className="d-flex justify-content-end">

                  <Button
                    color="primary"
                    onClick={
                      handleSave
                    }
                    disabled={saving}
                  >

                    {saving ? (
                      <Spinner
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <FiSave
                        size={15}
                        className="me-2"
                      />
                    )}

                    Save Changes

                  </Button>

                </div>

              </CardBody>

            </Card>


            {/* SECURITY */}

            <Card>

              <CardBody>

                <div className="d-flex align-items-center mb-3">

                  <FiShield
                    size={20}
                    className="text-primary me-2"
                  />

                  <div>

                    <h5 className="mb-1">
                      Security
                    </h5>

                    <p className="text-muted mb-0">
                      Account security settings.
                    </p>

                  </div>

                </div>


                <Alert
                  color="light"
                  className="mb-0"
                >
                  <strong>
                    Password management
                  </strong>

                  <div className="text-muted small mt-1">
                    Password changes will be
                    connected to the backend
                    authentication system in
                    the account-security phase.
                  </div>
                </Alert>

              </CardBody>

            </Card>

          </Col>

        </Row>

      </Container>
    </div>
  );
};

export default Profile;